import { z } from 'zod';
import { TicketKeySchema } from './tickets';
import { ApprovalModeSchema } from './workflow/rules';

/*
 * Approvals (S-07-09, S-07-10; ADR-0019 §3). `APPROVAL_MODES`, `ApprovalModeSchema` and
 * `ApproverSpecSchema` live in `workflow/rules.ts` because the `approval_required` validator
 * is defined there; this module reuses them for the runtime records. One `ticket_approval`
 * row per approver; `requestId` groups the rows of one request.
 */

export const APPROVAL_DECISIONS = ['pending', 'approved', 'rejected'] as const;
export const ApprovalDecisionStateSchema = z.enum(APPROVAL_DECISIONS);
export type ApprovalDecisionState = z.infer<typeof ApprovalDecisionStateSchema>;

export const APPROVAL_DECIDED_VIA = ['api', 'email', 'portal'] as const;
export const ApprovalDecidedViaSchema = z.enum(APPROVAL_DECIDED_VIA);
export type ApprovalDecidedVia = z.infer<typeof ApprovalDecidedViaSchema>;

export const APPROVAL_COMMENT_MAX = 2000;
/** Validity of the single-use email token (`ticket_approval.token_expires_at`): 7 days. */
export const APPROVAL_TOKEN_TTL_HOURS = 24 * 7;

/** Problem codes next to `approval.pending` (packages/domain execute.ts). */
export const APPROVAL_REJECTED = 'approval.rejected';
export const APPROVAL_ALREADY_DECIDED = 'approval.already_decided';
export const APPROVAL_TOKEN_INVALID = 'approval.token_invalid';

/** One approver's slot in a request. The email token hash is never exposed. */
export const ApprovalSchema = z.object({
  id: z.uuid(),
  ticketId: z.uuid(),
  transitionId: z.uuid(),
  requestId: z.uuid(),
  approverId: z.uuid(),
  mode: ApprovalModeSchema,
  decision: ApprovalDecisionStateSchema,
  comment: z.string().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decidedVia: ApprovalDecidedViaSchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

/** Body of `POST /v1/tickets/{id}/approvals/{aid}/approve` and `.../reject` (session caller). */
export const ApprovalDecisionSchema = z.strictObject({
  comment: z.string().trim().max(APPROVAL_COMMENT_MAX).optional(),
});
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;

/**
 * Decision from the email link (S-07-10): the token identifies approval and approver, is
 * single-use and expires after `APPROVAL_TOKEN_TTL_HOURS`; no session is needed.
 */
export const ApprovalTokenDecisionSchema = z.strictObject({
  token: z.string().min(32).max(256),
  decision: ApprovalDecisionStateSchema.exclude(['pending']),
  comment: z.string().trim().max(APPROVAL_COMMENT_MAX).optional(),
});
export type ApprovalTokenDecision = z.infer<typeof ApprovalTokenDecisionSchema>;

/** `GET /v1/approvals?mine=true&decision=pending` (defaults shown). */
export const ApprovalListQuerySchema = z.object({
  mine: z.stringbool().default(true),
  decision: ApprovalDecisionStateSchema.default('pending'),
});
export type ApprovalListQuery = z.infer<typeof ApprovalListQuerySchema>;

/** Inbox item: the approval plus what the approver needs to decide without opening the ticket. */
export const ApprovalInboxItemSchema = ApprovalSchema.extend({
  ticket: z.object({
    id: z.uuid(),
    key: TicketKeySchema,
    summary: z.string(),
    projectId: z.uuid(),
  }),
  /** Who raised the request (the transition's caller); null for automations. */
  requester: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  /** When the request was raised; age is derived client-side from this. */
  requestedAt: z.iso.datetime(),
});
export type ApprovalInboxItem = z.infer<typeof ApprovalInboxItemSchema>;

export const ApprovalInboxResponseSchema = z.object({
  items: z.array(ApprovalInboxItemSchema),
  nextCursor: z.string().nullable(),
});
export type ApprovalInboxResponse = z.infer<typeof ApprovalInboxResponseSchema>;

/** Pending request summary on a ticket (`GET /v1/tickets/{id}` → `pendingApprovals`, S-07-09). */
export const PendingApprovalRequestSchema = z.object({
  requestId: z.uuid(),
  transitionId: z.uuid(),
  mode: ApprovalModeSchema,
  approvals: z.array(ApprovalSchema.pick({ id: true, approverId: true, decision: true })),
});
export type PendingApprovalRequest = z.infer<typeof PendingApprovalRequestSchema>;

/**
 * One row of `GET /v1/tickets/{id}/approvals` (backend gap 1): every request on the ticket,
 * pending and decided, so the ticket detail panel can show the full approval history in one
 * call instead of cross-referencing `/v1/approvals`. `canDecide` is resolved server-side against
 * the caller (pending, and the caller is the named approver) so the UI never has to guess the
 * session-decide rule (`decideApproval`'s own `approverId` check, `packages/jobs/approval-ops.ts`).
 */
export const TicketApprovalSchema = z.object({
  id: z.uuid(),
  requestId: z.uuid(),
  transitionId: z.uuid(),
  approverId: z.uuid(),
  approverDisplayName: z.string(),
  mode: ApprovalModeSchema,
  decision: ApprovalDecisionStateSchema,
  comment: z.string().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decidedVia: ApprovalDecidedViaSchema.nullable(),
  createdAt: z.iso.datetime(),
  canDecide: z.boolean(),
});
export type TicketApproval = z.infer<typeof TicketApprovalSchema>;

export const TicketApprovalListResponseSchema = z.object({
  items: z.array(TicketApprovalSchema),
});
export type TicketApprovalListResponse = z.infer<typeof TicketApprovalListResponseSchema>;

/* ------------------------- email-token decisions (S-07-10, backend gap 2) ------------------------- */

/**
 * Token layout: `apv_<tenant id as 32 hex chars>_<32 base62 chars>`, the same shape as `stk_`
 * API-key secrets (`api-keys.ts`) and for the same reason — `ticket_approval` is RLS-scoped, so
 * the token itself must carry the tenant id for the lookup to run inside `withTenant`. Only the
 * SHA-256 of the whole token is ever stored (`ticket_approval.token_hash`); the raw token exists
 * only in the email it is minted for.
 */
export const APPROVAL_TOKEN_PREFIX = 'apv_';
/** Tenant id without dashes. */
export const APPROVAL_TOKEN_TENANT_HEX_LENGTH = 32;
/** Base62 characters of the random part. */
export const APPROVAL_TOKEN_SECRET_LENGTH = 32;
export const APPROVAL_TOKEN_PATTERN = /^apv_[0-9a-f]{32}_[0-9A-Za-z]{32}$/;
export const APPROVAL_TOKEN_BASE62 =
  '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export interface ParsedApprovalToken {
  tenantId: string;
  /** The 32 base62 characters. */
  random: string;
}

const hexToTenantId = (hex: string): string =>
  `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;

/** Splits a well-formed token into its parts; null when the shape is wrong (garbage input). */
export function parseApprovalToken(token: string): ParsedApprovalToken | null {
  if (!APPROVAL_TOKEN_PATTERN.test(token)) {
    return null;
  }
  const body = token.slice(APPROVAL_TOKEN_PREFIX.length);
  const hex = body.slice(0, APPROVAL_TOKEN_TENANT_HEX_LENGTH);
  const random = body.slice(APPROVAL_TOKEN_TENANT_HEX_LENGTH + 1);
  return { tenantId: hexToTenantId(hex), random };
}

/** Builds the token from its parts; `random` must be 32 base62 characters. */
export const formatApprovalToken = (tenantId: string, random: string): string =>
  `${APPROVAL_TOKEN_PREFIX}${tenantId.replace(/-/g, '').toLowerCase()}_${random}`;

/** `GET /v1/approvals/token/:token` state (no session; ADR-0019 §3 "lands on the portal page"). */
export const APPROVAL_TOKEN_STATES = ['pending', 'decided', 'expired', 'invalid'] as const;
export const ApprovalTokenStateSchema = z.enum(APPROVAL_TOKEN_STATES);
export type ApprovalTokenState = z.infer<typeof ApprovalTokenStateSchema>;

/** Enough context for the token-landing page to render without a session. */
export const ApprovalTokenApprovalSchema = z.object({
  id: z.uuid(),
  mode: ApprovalModeSchema,
  decision: ApprovalDecisionStateSchema,
  ticket: z.object({ key: TicketKeySchema, summary: z.string() }),
  /** Who raised the request; null for automations. */
  requester: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
});
export type ApprovalTokenApproval = z.infer<typeof ApprovalTokenApprovalSchema>;

/** Body of `GET /v1/approvals/token/:token`; `approval` is null only when `state` is `invalid`. */
export const ApprovalTokenViewSchema = z.object({
  state: ApprovalTokenStateSchema,
  approval: ApprovalTokenApprovalSchema.nullable(),
});
export type ApprovalTokenView = z.infer<typeof ApprovalTokenViewSchema>;

/** Body of `POST /v1/approvals/token/:token/decide` (token identifies approval + approver). */
export const ApprovalTokenDecideSchema = z.strictObject({
  decision: ApprovalDecisionStateSchema.exclude(['pending']),
  comment: z.string().trim().max(APPROVAL_COMMENT_MAX).optional(),
});
export type ApprovalTokenDecide = z.infer<typeof ApprovalTokenDecideSchema>;

export const ApprovalTokenDecideResponseSchema = z.object({
  decision: ApprovalDecisionStateSchema,
  requestOutcome: ApprovalDecisionStateSchema,
});
export type ApprovalTokenDecideResponse = z.infer<typeof ApprovalTokenDecideResponseSchema>;

/**
 * Aggregate outcome of one request under its mode: `any` → approved on the first approval,
 * rejected only when everyone rejected; `all` → rejected on the first rejection, approved when
 * nobody is pending. Pure; shared by the validator hook, the decision endpoint and the UI.
 */
export function approvalRequestOutcome(
  mode: z.infer<typeof ApprovalModeSchema>,
  decisions: readonly ApprovalDecisionState[],
): ApprovalDecisionState {
  if (decisions.length === 0) {
    return 'pending';
  }
  const approved = decisions.filter((d) => d === 'approved').length;
  const rejected = decisions.filter((d) => d === 'rejected').length;
  if (mode === 'any') {
    if (approved > 0) {
      return 'approved';
    }
    return rejected === decisions.length ? 'rejected' : 'pending';
  }
  if (rejected > 0) {
    return 'rejected';
  }
  return approved === decisions.length ? 'approved' : 'pending';
}
